import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import withAuth from "../../utils/withAuth";
import { fetchCallAllDataByIDAPI } from "../../utils/api";
import ContentWrapper from "../../components/contentwrapper/ContentWrapper";
import ReportNavBar from "../../components/ReportNavBar";

const SKELETON_COUNT = 10;

const SkeletonTableCard = () => (
    <div className="col-md-4 col-lg-3 col-sm-6 mb-4">
        <div className="card h-100 shadow-sm">
            <div className="card-body p-2">
                <div className="skeleton" style={{ height: 18, width: "70%", marginBottom: 10 }} />
                <div className="skeleton" style={{ height: 14, width: "100%", marginBottom: 6 }} />
                <div className="skeleton" style={{ height: 14, width: "100%", marginBottom: 6 }} />
                <div className="skeleton" style={{ height: 14, width: "80%", marginBottom: 6 }} />
            </div>
        </div>
    </div>
);

const TodayTeamCall = () => {

    const [process, setProcess] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const navigate = useNavigate();
    const ppcid = localStorage.getItem("ppcdata");

    useEffect(() => {
        if (!ppcid) return navigate("/");
        navigate("/today-team-report");
        getLiveCallWithProcessName(ppcid);

    }, [ppcid]);

    const getLiveCallWithProcessName = async (id) => {
        setIsLoading(true);
        try {
            const res = await fetchCallAllDataByIDAPI(`ppc-team-get--today-live-call-with-proccess-name-by-id/${id}`);
            if (res?.data?.status === 1) setProcess(res?.data?.process || []);
            else toast.error(res?.data?.message || "Failed to load process");
        } catch (e) {
            console.error(e);
            toast.error("Network/CORS error while loading process");
        } finally {
            setIsLoading(false);
        }
    };

    // Suffix → Person/Group name map (extend freely)
    const PERSON_BY_CODE = {
        K: "Vivek Kumar",
        R: "Pragya Singh",
        J: "Akash Deep",
        V: "Pradeep Verma",
        T: "Yanshi",
        N: "Deepika Sisodia",
        D: "Deepak Kumar Giri",
        S: "Sumit Saini",
        I: "Ranjan Kumar",
        B: "Navin Chaurasia",
        E: "Bhumika Gola",
        G: "Vimal Jadon",
        P: "Anurodh Baral",
        M: "Simrati Kaur",
        Z: "Manprit Sir",
        A: "Ankita",
        W: "Ishika Pokhriyal",
        U: "Om Kumar",
    };

    const PREFER_PREFIX = false;
    // Helper: choose best match → (optionally prefix-first), then last2, last, then prefix, then token/Other
    const deriveGroupName = (skillRaw) => {
        const skill = String(skillRaw || "").trim();
        if (!skill) return "Other";

        const parts = skill.split("_").filter(Boolean);
        const n = parts.length;

        const first = (parts[0] || "").toUpperCase();
        const last = (parts[n - 1] || "").toUpperCase();
        const last2 = n >= 2 ? `${(parts[n - 2] || "").toUpperCase()}_${last}` : null;

        // Prefix-first (if enabled)
        if (PREFER_PREFIX) {
            if (PERSON_BY_CODE[first]) return PERSON_BY_CODE[first];
            if (first) return first;
        }

        // Suffix matches (your existing behavior)
        if (last2 && PERSON_BY_CODE[last2]) return PERSON_BY_CODE[last2];
        if (PERSON_BY_CODE[last]) return PERSON_BY_CODE[last];

        // Fallback to prefix
        if (PERSON_BY_CODE[first]) return PERSON_BY_CODE[first];
        if (first) return first;

        return "Other";
    };

    // Build [{name, total, items:[{code, count}]}] like your screenshot
    const groupedData = useMemo(() => {
        const groups = {};
        (process || []).forEach((row) => {
            const code = String(row?.skillname || "");
            const count = Number(row?.unique_calls || 0);
            const groupName = deriveGroupName(code);

            if (!groups[groupName]) groups[groupName] = { name: groupName, total: 0, items: [] };
            groups[groupName].total += count;
            groups[groupName].items.push({ code, count });
        });

        // sort inside groups
        Object.values(groups).forEach((g) =>
            g.items.sort((a, b) => (b.count !== a.count ? b.count - a.count : a.code.localeCompare(b.code)))
        );

        // sort groups by total desc, then name
        return Object.values(groups).sort((a, b) =>
            b.total !== a.total ? b.total - a.total : a.name.localeCompare(b.name)
        );
    }, [process]);



    const liveCallCount = useMemo(
        () => (process || []).reduce((sum, r) => sum + (Number(r?.unique_calls) || 0), 0),
        [process]
    );

    return (
        <div className="misDashboard">
            <Helmet>
                <title>SATKARTAR::DASHBOARD</title>
            </Helmet>

            {/* tiny CSS for skeleton shimmer + compact table */}
            <style>{`
        .skeleton{position:relative;overflow:hidden;background:#eee;border-radius:6px}
        .skeleton:after{content:"";position:absolute;inset:0;transform:translateX(-100%);
          background:linear-gradient(90deg,transparent,rgba(255,255,255,.6),transparent);
          animation:shimmer 1.2s infinite}
        @keyframes shimmer{100%{transform:translateX(100%)}}
        .mini-table th,.mini-table td{font-size:12px; padding:6px 8px;}
      `}</style>

            <ContentWrapper>
                <div className="row">
                    <div className="col-12">
                        <ReportNavBar />
                    </div>
                </div>
                <div className="">
                    <h5 className="livecallcount">
                        Today Team Call Count({isLoading ? '...' : liveCallCount})
                    </h5>
                </div>
                {/* Small section cards like your screenshot */}
                <div className="row mt-3 livecallbody">
                    {isLoading
                        ? Array.from({ length: SKELETON_COUNT }).map((_, i) => <SkeletonTableCard key={`sk-${i}`} />)
                        : groupedData.map((group, idx) => (
                            <div className="col-md-4 col-lg-3 col-sm-6 mb-4" key={idx}>
                                <div className="card h-100 shadow-sm">
                                    <div className="card-body p-2" style={{ maxHeight: 260, overflowY: "auto" }}>
                                        <table className="table table-sm table-bordered mb-0 mini-table">
                                            <thead>
                                                <tr>
                                                    <th className="d-flex justify-content-between align-items-center">
                                                        <span className="groupname">{group.name}</span>
                                                        <span>Grand Total</span>
                                                    </th>
                                                    <th style={{ width: 60, textAlign: "right" }}>{group.total}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {group.items.map((item, ix) => (
                                                    <tr key={ix}>
                                                        <td>{item.code}</td>
                                                        <td style={{ textAlign: "right" }}>{item.count}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        ))}

                    {!isLoading && groupedData.length === 0 && (
                        <div className="col-12">
                            <div className="alert alert-info mb-0">No data for today.</div>
                        </div>
                    )}
                </div>
            </ContentWrapper>
        </div>
    );
}

export default withAuth(TodayTeamCall);